import { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  Chip,
  Grid,
  LinearProgress,
  Collapse,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import InfoIcon from '@mui/icons-material/Info'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import ScienceIcon from '@mui/icons-material/Science'
import { useFilters } from '../../context/FilterContext'

// Data quality warnings for data scientists
export function DataQualityBanner({ compact = false }) {
  const [expanded, setExpanded] = useState(true)
  const { filteredStats } = useFilters()
  const studyCount = filteredStats?.totalStudies || 0

  if (compact) {
    return (
      <Alert
        severity="info"
        icon={<ScienceIcon />}
        action={
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        }
        sx={{ mb: 2 }}
      >
        <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Data Quality Notes for Analysts
        </AlertTitle>
        <Collapse in={expanded}>
          <DataQualityDetails />
        </Collapse>
        {!expanded && (
          <Typography variant="body2">
            n={studyCount} field studies | Class imbalance present | Control group validated | Click to expand
          </Typography>
        )}
      </Alert>
    )
  }

  return (
    <Card variant="outlined" sx={{ mb: 3, borderColor: 'info.main' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ScienceIcon color="info" />
          <Typography variant="h6">Data Quality Context</Typography>
          <Chip label="For Data Scientists" size="small" color="info" />
        </Box>
        <DataQualityDetails />
      </CardContent>
    </Card>
  )
}

function DataQualityDetails() {
  const { filteredStats } = useFilters()
  const studyCount = filteredStats?.totalStudies || 0

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Sample Size */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="warning.dark" gutterBottom>
            SAMPLE SIZE LIMITATIONS
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><WarningAmberIcon color="warning" fontSize="small" /></ListItemIcon>
              <ListItemText
                primary={`n=${studyCount} studies`}
                secondary="Sufficient for descriptive statistics, limited for ML training"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Control group validated"
                secondary="1 study with identical treated/untreated units proves treatment effect"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Selection bias possible"
                secondary="Studies may over-represent successful outcomes"
              />
            </ListItem>
          </List>
        </Grid>

        {/* Class Imbalance */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="warning.dark" gutterBottom>
            CLASS IMBALANCE
          </Typography>
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">R-22 Refrigerant</Typography>
              <Typography variant="body2" fontWeight={600}>75%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={75} color="warning" sx={{ height: 6, borderRadius: 1 }} />
          </Box>
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">RTU Equipment</Typography>
              <Typography variant="body2" fontWeight={600}>43%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={43} color="warning" sx={{ height: 6, borderRadius: 1 }} />
          </Box>
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">NY + FL Geographic</Typography>
              <Typography variant="body2" fontWeight={600}>39%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={39} color="warning" sx={{ height: 6, borderRadius: 1 }} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Models trained on this data will be biased toward these categories.
          </Typography>
        </Grid>

        {/* Data Completeness */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="success.dark" gutterBottom>
            FIELD COVERAGE
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="kW/Ton" secondary="109/113 field studies (96%)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Efficiency %" secondary="110/113 field studies (97%)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="PE Certification" secondary="113/113 field studies (100%)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Annual Savings" secondary="113/113 field studies (100%)" />
            </ListItem>
          </List>
        </Grid>

        {/* Methodological Notes */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>
            Methodological Strengths
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="100% IPMVP Option B"
                secondary="Standardized measurement protocol"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="PE-certified measurements"
                secondary="Licensed engineer verification"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Before/After methodology"
                secondary="Each system compared to itself - standard for retrofit M&V"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Third-party validation"
                secondary="UL, FAU academic, government testing"
              />
            </ListItem>
          </List>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="caption" color="text.secondary">
        <strong>Recommendation:</strong> This corpus is suitable for RAG/retrieval, descriptive analytics,
        and fine-tuning. For predictive modeling, augment with synthetic data and implement uncertainty
        quantification. Consider the class imbalances when interpreting model outputs.
      </Typography>
    </Box>
  )
}

// Uncertainty display component for stats
export function UncertaintyDisplay({ value, stdDev, confidence = 0.95, unit = '%', label }) {
  const zScore = confidence === 0.95 ? 1.96 : confidence === 0.99 ? 2.576 : 1.645
  const margin = stdDev ? (stdDev * zScore).toFixed(1) : null

  return (
    <Box>
      <Typography variant="h4" component="span" fontWeight={700}>
        {value}{unit}
      </Typography>
      {margin && (
        <Tooltip title={`${Math.round(confidence * 100)}% confidence interval`}>
          <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 1 }}>
            ± {margin}{unit}
          </Typography>
        </Tooltip>
      )}
      {label && (
        <Typography variant="caption" display="block" color="text.secondary">
          {label}
        </Typography>
      )}
    </Box>
  )
}

// Limitations panel showing lower performers and failures
export function LimitationsPanel({ data }) {
  const [expanded, setExpanded] = useState(true)

  // Find lower performers (below 15% efficiency)
  const lowerPerformers = data?.filter(d => d.efficiency_pct && d.efficiency_pct < 15) || []
  const totalWithEfficiency = data?.filter(d => d.efficiency_pct) || []
  const lowerPerformerPct = totalWithEfficiency.length > 0
    ? ((lowerPerformers.length / totalWithEfficiency.length) * 100).toFixed(1)
    : 0

  return (
    <Alert
      severity="warning"
      icon={<WarningAmberIcon />}
      sx={{ mb: 2 }}
    >
      <AlertTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Limitations & Lower Performers</span>
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </AlertTitle>

      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>{lowerPerformers.length} studies ({lowerPerformerPct}%)</strong> showed &lt;15% efficiency improvement.
        Not all applications achieve high results.
      </Typography>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Known Limitations:</Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><ErrorIcon color="error" fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="New equipment (&lt;2 years)"
                secondary="Minimal oil fouling = minimal improvement potential"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><ErrorIcon color="error" fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Recently serviced systems"
                secondary="Fresh oil charge reduces treatment effectiveness"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><ErrorIcon color="error" fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Mechanical issues"
                secondary="Treatment doesn't fix compressor/valve failures"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><WarningAmberIcon color="warning" fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Variable load profiles"
                secondary="Light-load conditions show less measurable improvement"
              />
            </ListItem>
          </List>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Screening Criteria:</Typography>
          <Typography variant="body2" color="text.secondary">
            Professional engineers screen candidates based on: equipment age (&gt;3 years recommended),
            operating hours, maintenance history, and existing efficiency baseline. Not all systems
            are good candidates for treatment.
          </Typography>
        </Box>
      </Collapse>
    </Alert>
  )
}

export default DataQualityBanner
